"use client";

import { useActionsTrack } from "@/_old/features/analytics";
import { useAuth } from "@/core/components/auth/store/auth";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/core/components/ui/accordion";
import { useDashboardFilters } from "@/dashboard/store/useDashboardFilters";
import { useEffect } from "react";
import { CheckInConfirmedBookings } from "./components/check-in-confirmed-bookings/check-in-confirmed-bookings";
import { DayUseBookings } from "./components/day-use-bookings/day-use-bookings";
import {
  ExceededCreditsCard,
  ExceededCreditsCardSkeleton,
  useExceededCreditsCard,
} from "./components/exceeded-credits-card";
import { ExcessConsumption } from "./components/excess-consumption/excess-consumption";
import { InsightsConsumption } from "./components/insights-consumption/insights-consumption";

export type ExceededCardState = {
  visible: boolean;
  loading: boolean;
};

type DashboardInsightsProps = {
  onExceededCardStateChange?: (state: ExceededCardState) => void;
};

export function DashboardInsights({
  onExceededCardStateChange,
}: DashboardInsightsProps) {
  const { userInfo } = useAuth();
  const { onActionClick } = useActionsTrack();
  const companyId = userInfo?.league?.id;
  const companyName =
    userInfo?.league?.name ??
    userInfo?.league?.legal_name ??
    userInfo?.user?.company_name ??
    "";
  const userId = userInfo?.user?.id;
  const userEmail = userInfo?.profile?.email;
  const { filters } = useDashboardFilters();

  const exceededCardHookResult = useExceededCreditsCard({
    companyId: companyId ?? 0,
    companyName,
    userId,
    userEmail,
    onActionClick,
  });

  useEffect(() => {
    const visible = exceededCardHookResult.state !== "initial";
    const loading = exceededCardHookResult.isExceededCardDataLoading ?? false;
    onExceededCardStateChange?.({ visible, loading });
  }, [
    exceededCardHookResult.state,
    exceededCardHookResult.isExceededCardDataLoading,
    onExceededCardStateChange,
  ]);

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue="item-1"
      className="w-full border-none"
    >
      <AccordionItem
        value="item-1"
        className="bg-white rounded-[16px] border-none shadow-sm"
      >
        <AccordionTrigger className="px-4 mb-4 no-underline border-b cursor-pointer hover:no-underline">
          <div className="flex gap-2 items-center">
            <h3 className="font-normal text-md">Insights</h3>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4">
          {/* auto-fit c/ min 226px: os cards descem de linha conforme o espaço
              encolhe (sidebar + painel do agente abertos) */}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(226px,1fr))] gap-4 [&>*]:rounded-[8px] [&_[data-slot=card]]:rounded-[8px]">
            {exceededCardHookResult.isExceededCardDataLoading ? (
              <ExceededCreditsCardSkeleton />
            ) : exceededCardHookResult.state !== "initial" ? (
              <ExceededCreditsCard
                companyId={companyId ?? 0}
                companyName={companyName}
                userId={userId}
                userEmail={userEmail}
                hookResult={exceededCardHookResult}
              />
            ) : (
              <ExcessConsumption
                startDate={filters.startDate}
                endDate={filters.endDate}
                companyId={companyId}
              />
            )}

            <InsightsConsumption
              startDate={filters.startDate}
              endDate={filters.endDate}
              companyId={companyId}
            />

            <CheckInConfirmedBookings
              startDate={filters.startDate}
              endDate={filters.endDate}
              companyId={companyId}
            />

            <DayUseBookings
              startDate={filters.startDate}
              endDate={filters.endDate}
              companyId={companyId}
            />
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
